import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isStrongPassword(pw)) return setError("Kies een wachtwoord van minimaal 8 tekens met minstens 1 cijfer.");
    if (pw !== pw2) return setError("De twee wachtwoorden zijn niet gelijk.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Je wachtwoord is opnieuw ingesteld. Log in met je nieuwe wachtwoord.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0b0d10] px-4 py-10">
      <img src="/login-lux-bg.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0d10]/80 via-[#0b0d10]/70 to-[#0b0d10]/90" />
      <div
        data-testid="reset-card"
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/60 bg-white/95 p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-2xl sm:p-11"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-6 rounded-2xl bg-white px-6 py-4 shadow-sm ring-1 ring-slate-100">
            <img src="/clazing-logo.png" alt="Clazing Convenience Food" className="h-16 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Nieuw wachtwoord instellen</h1>
          <p className="mt-2 text-sm text-slate-500">Kies een nieuw wachtwoord van minimaal 8 tekens met minstens 1 cijfer.</p>
        </div>

        {!token ? (
          <div data-testid="reset-no-token" className="mt-7 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Deze link is ongeldig. Vraag een nieuwe herstel-link aan via "Wachtwoord vergeten".
          </div>
        ) : (
          <>
            {error && (
              <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="reset-error">{error}</div>
            )}
            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nieuw wachtwoord</Label>
                <PasswordInput
                  data-testid="reset-pw"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1.5 h-11 rounded-xl border-slate-200 bg-slate-50/60"
                  toggleTestId="reset-pw-toggle"
                  autoFocus
                  required
                />
                <PasswordStrength value={pw} />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Herhaal wachtwoord</Label>
                <PasswordInput
                  data-testid="reset-pw2"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1.5 h-11 rounded-xl border-slate-200 bg-slate-50/60"
                  toggleTestId="reset-pw2-toggle"
                  required
                />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="h-12 w-full rounded-xl bg-slate-900 text-[15px] font-semibold text-white hover:bg-slate-800">
                {loading ? "Bezig..." : "Wachtwoord instellen"}
              </Button>
            </form>
          </>
        )}

        <Link to="/login" data-testid="reset-back" className="mt-7 flex items-center justify-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-3.5 w-3.5" /> Terug naar inloggen
        </Link>
      </div>
    </div>
  );
}
